import axios from 'axios';
import { AuthBodyType } from '../types';
import { Agendamento, Cadastro, CadastroModelo, CampoCadastroRespondido, ModeloAgendamento, PaginaCursor } from '../types/fluxo/types';
import { applyFirebaseTokenRetry } from './api';

type ListaModelosCadastroResponse = {
  cadastroModelos?: CadastroModelo[];
  cadastrosModelo?: CadastroModelo[];
  modelos?: CadastroModelo[];
};

type ListaCadastrosResponse = { cadastros: Cadastro[] };
type ListaModelosAgendamentoResponse = {
  modelosAgendamento?: ModeloAgendamento[];
  agendamentoModelos?: ModeloAgendamento[];
  agendamentosModelo?: ModeloAgendamento[];
  modelos?: ModeloAgendamento[];
};
type ListaAgendamentosResponse = { agendamentos: Agendamento[] };

class CadastrosAgendamentos {
  private api = applyFirebaseTokenRetry(axios.create({ baseURL: `${process.env.REACT_APP_AXYMA_MICROSSERVICES_APIS_URL}/empresa` }));

  private config(token: AuthBodyType) {
    return { headers: { ...token } };
  }

  listarModelosCadastro = async (token: AuthBodyType) => {
    const { data } = await this.api.get<ListaModelosCadastroResponse>('/cadastro-modelo', this.config(token));
    return data.cadastroModelos ?? data.cadastrosModelo ?? data.modelos ?? [];
  };

  listarCadastros = async (token: AuthBodyType, modelo: string) => {
    const { data } = await this.api.get<ListaCadastrosResponse>('/cadastro', {
      ...this.config(token), params: { modelo },
    });
    return data.cadastros;
  };

  listarModelosAgendamento = async (token: AuthBodyType) => {
    const { data } = await this.api.get<ListaModelosAgendamentoResponse>('/agendamento-modelo', this.config(token));
    return data.modelosAgendamento ?? data.agendamentoModelos ?? data.agendamentosModelo ?? data.modelos ?? [];
  };

  criarCadastro = async (token: AuthBodyType, cadastro: { idModelo: string; nome: string; campos: CampoCadastroRespondido[] }) => {
    const { data } = await this.api.post<{ success: true; cadastro: Cadastro }>('/cadastro', {
      empresa: token.userEmpresa,
      ...cadastro,
    }, this.config(token));
    return data.cadastro;
  };

  editarCadastro = async (token: AuthBodyType, id: string, cadastro: { idModelo: string; nome: string; campos: CampoCadastroRespondido[] }) => {
    const { data } = await this.api.patch<{ success: true; cadastro: Cadastro }>(`/cadastro/${id}`, {
      empresa: token.userEmpresa,
      ...cadastro,
    }, this.config(token));
    return data.cadastro;
  };

  excluirCadastro = async (token: AuthBodyType, id: string) => {
    const { data } = await this.api.delete<{ success: true; cadastro: Cadastro }>(`/cadastro/${id}`, this.config(token));
    return data.cadastro;
  };

  criarAgendamento = async (token: AuthBodyType, agendamento: {
    idModeloAgendamento: string;
    identificador: string;
    cadastros: { idRegraCadastro: string; idCadastro: string; idModeloCadastro: string }[];
    idFluxo: string;
    idChecklist: string;
    versaoFluxo?: number;
    idUnidade: string;
    dataPrevista: number;
    chaveOrigem: string;
    payloadReferencia: Record<string, unknown>;
  }) => {
    const { data } = await this.api.post<{ success: true }>('/agendamento', {
      empresa: token.userEmpresa,
      origem: 'admin',
      ...agendamento,
    }, {
      headers: {
        ...token,
        Authorization: `Bearer ${token.userToken}`,
      },
    });
    return data;
  };

  listarAgendamentos = async (token: AuthBodyType) => {
    const { data } = await this.api.get<ListaAgendamentosResponse>('/agendamento', this.config(token));
    return data.agendamentos;
  };

  listarModelosAgendamentoPaginado = async (token: AuthBodyType, limite = 100, cursor?: string) => {
    const { data } = await this.api.get<PaginaCursor<ModeloAgendamento>>('/agendamento-modelo/paginado', {
      ...this.config(token), params: { limite, ...(cursor ? { cursor } : {}) },
    });
    return data;
  };
}

export default CadastrosAgendamentos;
